


import React, { useState, useEffect } from "react";
import "./datatable.scss";
import { Api_Url } from "../../../../utils/ApiUrl";
import { toast } from "react-toastify";
import Loader from "../../../../hooks/loader";
import { Accordion } from "react-bootstrap";

const DataTable = ({ isAdmin }) => {
  // Per-client resend state so each row is independent
  const [resendingIds, setResendingIds] = useState({});
  const [sentIds, setSentIds] = useState({});
  // Rows that hit the "email already sent" limit — button stays disabled
  const [cooldownIds, setCooldownIds] = useState({});

  const handleResend = async (clientId) => {
    setResendingIds((prev) => ({ ...prev, [clientId]: true }));

    try {
      const response = await fetch(
        `${ApiUrl}/project-admin/${clientId}/resend-email`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok && (data.statusCode === 200 || data.statusCode === 201)) {
        toast.success(data.message || "Invitation resent successfully");
        setSentIds((prev) => ({ ...prev, [clientId]: true }));
      } else if (
        response.status === 409 ||
        data.statusCode === 409 ||
        /already|wait|minute/i.test(data.message || "")
      ) {
        // Email was already sent recently — inform the user and disable the button
        toast.error(
          "An invitation email has already been sent. Please try again in 30 minutes.",
        );
        setCooldownIds((prev) => ({ ...prev, [clientId]: true }));
      } else {
        toast.error(data.message || "Resend invite failed");
      }
    } catch (error) {
      console.error("Error resending invitation:", error);
      toast.error("Resend invite failed");
    } finally {
      setResendingIds((prev) => ({ ...prev, [clientId]: false }));
    }
  };


  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
  });
  console.log(clientData, 'clientData');
  const [loadingToggles, setLoadingToggles] = useState({});

  const ApiUrl = Api_Url;

  // Mobile accordion state - now properly manages multiple accordions
  const [openAccordionKey, setOpenAccordionKey] = useState(null);

  const toggleAccordion = (key) => {
    setOpenAccordionKey((prevKey) => (prevKey === key ? null : key));
  };

  // Fetch client data
  const fetchClientData = async (page = 1) => {
    setLoading(true);
    try {
      const offset = page;
      const response = await fetch(
        `${ApiUrl}/project-admin/client?limit=${pagination.limit}&offset=${offset}&orderField=createdAt&orderDirection=-1`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      const data = await response.json();

      if (response.ok && data.statusCode === 200) {
        setClientData(data.data.clientAdmin);
        setPagination((prev) => ({
          ...prev,
          currentPage: page,
          totalPages: data.data.pages,
          totalCount: data.data.count,
        }));
      } else {
        toast.error(data.message || "Failed to fetch client data");
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle CSV permission
  const toggleCsvPermission = async (clientId, currentStatus) => {
    const toggleKey = `csv_${clientId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await fetch(`${ApiUrl}/project-admin/client/csv`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ id: clientId }),
      });

      const data = await response.json();

      if (response.ok && data.statusCode === 201) {
        toast.success("CSV access updated successfully");
        setClientData((prev) =>
          prev.map((client) =>
            client._id === clientId
              ? { ...client, isCsvAllowed: data.data.isCsvAllowed }
              : client,
          ),
        );
      } else {
        toast.error(data.message || "Failed to toggle CSV access");
      }
    } catch (error) {
      console.error("Error toggling CSV permission:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [toggleKey]: false }));
    }
  };

  // Toggle client activation status
  const toggleClientStatus = async (clientId, currentStatus) => {
    const toggleKey = `status_${clientId}`;
    setLoadingToggles((prev) => ({ ...prev, [toggleKey]: true }));

    try {
      const response = await fetch(`${ApiUrl}/project-admin/client/isEnabled`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ id: clientId }),
      });

      const data = await response.json();

      if (response.ok && data.statusCode === 201) {
        toast.success("Client status updated successfully");
        setClientData((prev) =>
          prev.map((client) =>
            client._id === clientId
              ? { ...client, isEnabled: data.data.isEnabled }
              : client,
          ),
        );
      } else {
        toast.error(data.message || "Failed to toggle client status");
      }
    } catch (error) {
      console.error("Error toggling client status:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [toggleKey]: false }));
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy email");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return { date: formattedDate, time: formattedTime };
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && !loading) {
      fetchClientData(newPage);
    }
  };

  // Generate pagination range
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, pagination.currentPage - delta);
      i <= Math.min(pagination.totalPages - 1, pagination.currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (pagination.currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (pagination.currentPage + delta < pagination.totalPages - 1) {
      rangeWithDots.push("...", pagination.totalPages);
    } else {
      if (pagination.totalPages > 1) {
        rangeWithDots.push(pagination.totalPages);
      }
    }

    return rangeWithDots;
  };

  // Fetch initial data
  useEffect(() => {
    fetchClientData();
  }, []);

  return (
    <>
      {loading && <Loader />}
      <section className="datatable clientsdatable">
        {/* Desktop Table */}
        <div className="table-responsive d-none d-md-block justmobiled-none">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <div className="parent-tag">Date Added</div>
                </th>
                <th>Email</th>
                <th>Name</th>
                <th>Project Assigned</th>
                {isAdmin ? (
                  <th className="text-center">CSV Permission</th>
                ) : null}
                {isAdmin ? (
                  <th className="text-center">Activate/Deactivate</th>
                ) : null}

                <th>Invitation</th>
              </tr>
            </thead>
            <tbody>
              {clientData.length === 0 && !loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No clients found
                  </td>
                </tr>
              ) : (
                clientData?.map((client) => {
                  const { date, time } = formatDate(client.createdAt);
                  return (
                    <tr key={client._id}>
                      <td>
                        <div className="twice-text">
                          <h6>{date}</h6>
                          <p>{time}</p>
                        </div>
                      </td>
                      <td>
                        <p className="codepara">
                          {client.email}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="15"
                            height="16"
                            viewBox="0 0 15 16"
                            fill="none"
                            style={{ cursor: "pointer", marginLeft: "8px" }}
                            onClick={() => copyToClipboard(client.email)}
                          >
                            <path
                              d="M10.0139 8.49985L10.0402 10.9497C10.0622 12.9913 9.25441 13.8167 7.21286 13.8386L4.763 13.865C2.72145 13.887 1.89604 13.0792 1.87405 11.0376L1.84767 8.58778C1.82569 6.54623 2.63351 5.72082 4.67506 5.69884L7.12492 5.67246C9.16647 5.65047 9.99188 6.4583 10.0139 8.49985Z"
                              stroke="#77868B"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M13.476 4.96235L13.5024 7.41221C13.5244 9.45376 12.7166 10.2792 10.675 10.3012L10.0334 10.3081L10.0139 8.49984C9.99193 6.45829 9.16652 5.65046 7.12497 5.67245L5.31674 5.69192L5.30983 5.05029C5.28785 3.00874 6.09567 2.18333 8.13722 2.16135L10.5871 2.13496C12.6286 2.11298 13.454 2.92081 13.476 4.96235Z"
                              stroke="#77868B"
                              strokeWidth="1.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </p>
                      </td>
                      <td>{client.name || "N/A"}</td>
                      <td>
                        <div className="maintoken">
                          <div className="tokenimg">
                            <img
                              src={client?.projectImage || `/assets/${client.privateSale.toLowerCase()}.svg`}
                              alt="project logo"
                              className="innerimg"
                              onError={(e) => {
                                e.target.src = client?.projectImage || "/assets/default.svg";
                              }}
                            />
                          </div>
                          <p className="tokenpara">{client.privateSale}</p>
                        </div>
                      </td>
                      {isAdmin ? (
                        <td>
                          <div className="parent-toggle-switch-custom tabletoggle">
                            <div className="custom-toggle">
                              <input
                                type="checkbox"
                                className="checkbox"
                                id={`csv-toggle-${client._id}`}
                                checked={client.isCsvAllowed}
                                onChange={() =>
                                  toggleCsvPermission(
                                    client._id,
                                    client.isCsvAllowed,
                                  )
                                }
                                disabled={loadingToggles[`csv_${client._id}`]}
                              />
                              <label
                                className="switch"
                                htmlFor={`csv-toggle-${client._id}`}
                              >
                                <span className="slider"></span>
                              </label>
                            </div>
                          </div>
                        </td>
                      ) : null}
                      {isAdmin ? (
                        <td>
                          <div className="parent-toggle-switch-custom tabletoggle">
                            <div className="custom-toggle">
                              <input
                                type="checkbox"
                                className="checkbox"
                                id={`status-toggle-${client._id}`}
                                checked={client.isEnabled}
                                onChange={() =>
                                  toggleClientStatus(
                                    client._id,
                                    client.isEnabled,
                                  )
                                }
                                disabled={
                                  loadingToggles[`status_${client._id}`]
                                }
                              />
                              <label
                                className="switch"
                                htmlFor={`status-toggle-${client._id}`}
                              >
                                <span className="slider"></span>
                              </label>
                            </div>
                          </div>
                        </td>
                      ) : null}
                      <td>
                        {" "}
                        <div className="buttons_table">
                          {sentIds[client._id] ? (
                            <button className="sent" disabled>
                              Sent
                            </button>
                          ) : cooldownIds[client._id] ? (
                            <button
                              className="resend"
                              disabled
                              title="An invitation email has already been sent. Please try again in 30 minutes."
                            >
                              Try again in 30 min
                            </button>
                          ) : (
                            <button
                              className="resend"
                              onClick={() => handleResend(client._id)}
                              disabled={resendingIds[client._id]}
                            >
                              {resendingIds[client._id]
                                ? "Sending..."
                                : "Resend Invitation"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Accordions */}
        <div className="mobileaccordions d-block d-md-none">
          {clientData.length === 0 && !loading ? (
            <div className="text-center py-4">
              <p>No clients found</p>
            </div>
          ) : (
            clientData.map((client, index) => {
              const { date, time } = formatDate(client.createdAt);
              const accordionKey = client._id;

              return (
                <div key={client._id} className="inneracc">
                  <div className="accheader">
                    <div className="accmain">
                      <p className="accpara">Date Added</p>
                      <h6 className="acchead">
                        {date} {time}
                      </h6>
                    </div>
                    <div className="accmain">
                      <p className="accpara">Email</p>
                      <h6 className="acchead">
                        {client.email}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 15 15"
                          fill="none"
                          style={{ cursor: "pointer", marginLeft: "8px" }}
                          onClick={() => copyToClipboard(client.email)}
                        >
                          <path
                            d="M9.41377 7.57483L9.44015 10.0247C9.46213 12.0662 8.65431 12.8916 6.61276 12.9136L4.1629 12.94C2.12135 12.962 1.29594 12.1542 1.27396 10.1126L1.24757 7.66276C1.22559 5.62122 2.03342 4.7958 4.07496 4.77382L6.52482 4.74744C8.56637 4.72545 9.39178 5.53328 9.41377 7.57483Z"
                            stroke="#77868B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12.8759 4.03735L12.9023 6.48721C12.9243 8.52876 12.1165 9.35417 10.0749 9.37616L9.43329 9.38306L9.41382 7.57484C9.39183 5.53329 8.56642 4.72546 6.52487 4.74745L4.71664 4.76692L4.70973 4.12529C4.68775 2.08374 5.49557 1.25833 7.53712 1.23634L9.98698 1.20996C12.0285 1.18798 12.8539 1.9958 12.8759 4.03735Z"
                            stroke="#77868B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </h6>
                    </div>
                    <div className="accmain">
                      <p className="accpara">Name</p>
                      <h6 className="acchead">{client.name || "N/A"}</h6>
                    </div>
                    <div className="accmain">
                      <p className="accpara">Project Assigned</p>
                      <div className="mainproject">
                        <div className="projectimg">
                          <img
                            src={`/assets/${client.privateSale.toLowerCase()}.svg`}
                            alt="project logo"
                            className="innerimg"
                            onError={(e) => {
                              e.target.src = "/assets/default.svg";
                            }}
                          />
                        </div>
                        <p className="projectpara">{client.privateSale}</p>
                      </div>
                    </div>
                  </div>

                  <Accordion activeKey={openAccordionKey}>
                    <Accordion.Collapse eventKey={accordionKey}>
                      <div className="maincontent">
                        <div className="innercontent">
                          <p className="contentpara">CSV Permission</p>
                          <div className="parent-toggle-switch-custom tabletoggle">
                            <div className="custom-toggle">
                              <input
                                type="checkbox"
                                className="checkbox"
                                id={`mobile-csv-toggle-${client._id}`}
                                checked={client.isCsvAllowed}
                                onChange={() =>
                                  toggleCsvPermission(
                                    client._id,
                                    client.isCsvAllowed,
                                  )
                                }
                                disabled={loadingToggles[`csv_${client._id}`]}
                              />
                              <label
                                className="switch"
                                htmlFor={`mobile-csv-toggle-${client._id}`}
                              >
                                <span className="slider"></span>
                              </label>
                            </div>
                            {loadingToggles[`csv_${client._id}`] && (
                              <div className="toggle-loading ms-2">
                                <small>Loading...</small>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="innercontent">
                          <p className="contentpara">Activate/Deactivate</p>
                          <div className="parent-toggle-switch-custom tabletoggle">
                            <div className="custom-toggle">
                              <input
                                type="checkbox"
                                className="checkbox"
                                id={`mobile-status-toggle-${client._id}`}
                                checked={client.isEnabled}
                                onChange={() =>
                                  toggleClientStatus(
                                    client._id,
                                    client.isEnabled,
                                  )
                                }
                                disabled={
                                  loadingToggles[`status_${client._id}`]
                                }
                              />
                              <label
                                className="switch"
                                htmlFor={`mobile-status-toggle-${client._id}`}
                              >
                                <span className="slider"></span>
                              </label>
                            </div>
                            {loadingToggles[`status_${client._id}`] && (
                              <div className="toggle-loading ms-2">
                                <small>Loading...</small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Accordion.Collapse>
                  </Accordion>

                  {isAdmin ? (
                    <button
                      onClick={() => toggleAccordion(accordionKey)}
                      className="accbutton"
                    >
                      {openAccordionKey === accordionKey
                        ? "Less Info"
                        : "More Info"}
                      {openAccordionKey === accordionKey ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="6"
                          viewBox="0 0 10 6"
                          fill="none"
                        >
                          <path
                            d="M8.62344 5.22141H4.80933H1.3748C0.787069 5.22141 0.493206 4.44081 0.909513 3.98322L4.08079 0.497446C4.58893 -0.0610847 5.41543 -0.0610847 5.92356 0.497446L7.12963 1.82312L9.09485 3.98322C9.50503 4.44081 9.21117 5.22141 8.62344 5.22141Z"
                            fill="#02171D"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="9"
                          height="6"
                          viewBox="0 0 9 6"
                          fill="none"
                        >
                          <path
                            d="M8.12344 0.228516H4.30933H0.874797C0.287069 0.228516 -0.00679424 1.00911 0.409513 1.4667L3.58079 4.95247C4.08893 5.51101 4.91543 5.51101 5.42356 4.95247L6.62963 3.62681L8.59485 1.4667C9.00503 1.00911 8.71117 0.228516 8.12344 0.228516Z"
                            fill="#02171D"
                          />
                        </svg>
                      )}
                    </button>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination - Show for both mobile and desktop */}
        {clientData.length > 0 && (
          <div className="d-block">
            <div className="footer-content d-block">
              <div className="row">
                <div className="col-12 col-md-6">
                  <div className="left-f">
                    <h6 className="d-none d-md-block">
                      SHOWING{" "}
                      {(pagination.currentPage - 1) * pagination.limit + 1}-
                      {Math.min(
                        pagination.currentPage * pagination.limit,
                        pagination.totalCount,
                      )}{" "}
                      OF {pagination.totalCount}
                    </h6>
                    <p className="d-block d-md-none text-center mb-3">
                      {(pagination.currentPage - 1) * pagination.limit + 1}-
                      {Math.min(
                        pagination.currentPage * pagination.limit,
                        pagination.totalCount,
                      )}{" "}
                      of {pagination.totalCount}
                    </p>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="right-f d-flex justify-content-center justify-content-md-end">
                    <nav aria-label="Page navigation">
                      <ul className="pagination flex-wrap justify-content-center">
                        <li
                          className={`page-item ${pagination.currentPage === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              handlePageChange(pagination.currentPage - 1)
                            }
                            disabled={pagination.currentPage === 1 || loading}
                            aria-label="Previous page"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="17"
                              viewBox="0 0 17 17"
                              fill="none"
                            >
                              <path
                                d="M7.88904 2.43555L5.38763 2.43555L1.99459 8.49682L4.496 8.49682L7.88904 2.43555Z"
                                fill="#869397"
                              />
                              <path
                                d="M7.88904 14.5703L5.38763 14.5703L1.99459 8.50904L3.57108 7.46117L4.496 8.50904L7.88904 14.5703Z"
                                fill="#869397"
                              />
                              <path
                                d="M8.01655 7.35742H14.9616L13.6869 9.64313H6.74182L8.01655 7.35742Z"
                                fill="#869397"
                              />
                            </svg>
                          </button>
                        </li>

                        {getPaginationRange().map((page, index) => (
                          <li
                            key={index}
                            className={`page-item ${page === pagination.currentPage ? "active" : ""} ${page === "..." ? "disabled" : ""}`}
                          >
                            {page === "..." ? (
                              <span className="page-link">...</span>
                            ) : (
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(page)}
                                disabled={loading}
                                aria-label={`Go to page ${page}`}
                              >
                                {page}
                              </button>
                            )}
                          </li>
                        ))}

                        <li
                          className={`page-item ${pagination.currentPage === pagination.totalPages ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              handlePageChange(pagination.currentPage + 1)
                            }
                            disabled={
                              pagination.currentPage ===
                              pagination.totalPages || loading
                            }
                            aria-label="Next page"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="17"
                              height="17"
                              viewBox="0 0 17 17"
                              fill="none"
                            >
                              <path
                                d="M9.11108 2.43555L11.6125 2.43555L15.0055 8.49682L12.5041 8.49682L9.11108 2.43555Z"
                                fill="#329879"
                              />
                              <path
                                d="M9.11108 14.5703L11.6125 14.5703L15.0055 8.50904L13.429 7.46117L12.5041 8.50904L9.11108 14.5703Z"
                                fill="#329879"
                              />
                              <path
                                d="M3.31318 7.35742H10.2582L8.98351 9.64313H2.03845L3.31318 7.35742Z"
                                fill="#329879"
                              />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default DataTable;

// import React, { useState, useEffect } from "react";
// import "./datatable.scss";
// import { Api_Url } from "../../../../utils/ApiUrl";
// import { toast } from "react-toastify";
// import Loader from "../../../../hooks/loader";
// import { Accordion } from "react-bootstrap";

// const DataTable = () => {
//   const [clientData, setClientData] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [pagination, setPagination] = useState({
//     currentPage: 1,
//     totalPages: 1,
//     totalCount: 0,
//     limit: 10
//   });
//   const [loadingToggles, setLoadingToggles] = useState({});

//   const ApiUrl = Api_Url;
//   const [toggles, setToggles] = useState({
//     toggle1: false,
//     toggle2: false,
//     toggle3: false,
//   });

//   const handleToggle = (key) => {
//     setToggles((prev) => ({
//       ...prev,
//       [key]: !prev[key],
//     }));
//   };

//   const [openKey, setOpenKey] = useState(null);

//   const toggle = (key) => {
//     setOpenKey(prevKey => (prevKey === key ? null : key));
//   };
//   // Fetch client data
//   const fetchClientData = async (page = 1) => {
//     setLoading(true);
//     try {
//       const offset = page //(page - 1) * pagination.limit + 1;
//       const response = await fetch(
//         `${ApiUrl}/project-admin/client?limit=${pagination.limit}&offset=${offset}&orderField=createdAt&orderDirection=-1`,
//         {
//           headers: {
//             'Authorization': `Bearer ${localStorage.getItem('accessToken')}`, // Added Authorization header
//           }
//         }
//       );

//       const data = await response.json();

//       if (response.ok && data.statusCode === 200) {
//         setClientData(data.data.clientAdmin);
//         setPagination(prev => ({
//           ...prev,
//           currentPage: page,
//           totalPages: data.data.pages,
//           totalCount: data.data.count
//         }));
//       } else {
//         toast.error(data.message || "Failed to fetch client data");
//       }
//     } catch (error) {
//       console.error('Error fetching client data:', error);
//       toast.error("Network error. Please check your connection.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Toggle CSV permission
//   const toggleCsvPermission = async (clientId, currentStatus) => {
//     const toggleKey = `csv_${clientId}`;
//     setLoadingToggles(prev => ({ ...prev, [toggleKey]: true }));

//     try {
//       const response = await fetch(`${ApiUrl}/project-admin/client/csv`, {
//         method: 'PATCH',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
//         },
//         body: JSON.stringify({ id: clientId })
//       });

//       const data = await response.json();

//       if (response.ok && data.statusCode === 201) {
//         toast.success("CSV access toggled successfully");
//         // Update local state
//         setClientData(prev =>
//           prev.map(client =>
//             client._id === clientId
//               ? { ...client, isCsvAllowed: data.data.isCsvAllowed }
//               : client
//           )
//         );
//       } else {
//         toast.error(data.message || "Failed to toggle CSV access");
//       }
//     } catch (error) {
//       console.error('Error toggling CSV permission:', error);
//       toast.error("Network error. Please try again.");
//     } finally {
//       setLoadingToggles(prev => ({ ...prev, [toggleKey]: false }));
//     }
//   };

//   // Toggle client activation status
//   const toggleClientStatus = async (clientId, currentStatus) => {
//     const toggleKey = `status_${clientId}`;
//     setLoadingToggles(prev => ({ ...prev, [toggleKey]: true }));

//     try {
//       // Note: The API endpoint seems to be the same for both toggles based on your spec
//       // You might need to adjust this to the correct endpoint for client activation
//       const response = await fetch(`${ApiUrl}/project-admin/client/isEnabled`, {
//         method: 'PATCH',
//         headers: {
//           'Content-Type': 'application/json',
//           'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
//         },
//         body: JSON.stringify({ id: clientId })
//       });

//       const data = await response.json();

//       if (response.ok && data.statusCode === 201) {
//         toast.success("Client status toggled successfully");
//         // Update local state
//         setClientData(prev =>
//           prev.map(client =>
//             client._id === clientId
//               ? { ...client, isEnabled: data.data.isEnabled }
//               : client
//           )
//         );
//       } else {
//         toast.error(data.message || "Failed to toggle client status");
//       }
//     } catch (error) {
//       console.error('Error toggling client status:', error);
//       toast.error("Network error. Please try again.");
//     } finally {
//       setLoadingToggles(prev => ({ ...prev, [toggleKey]: false }));
//     }
//   };

//   // Copy to clipboard function
//   const copyToClipboard = async (text) => {
//     try {
//       await navigator.clipboard.writeText(text);
//       toast.success("Email copied to clipboard!");
//     } catch (error) {
//       console.error('Failed to copy:', error);
//       toast.error("Failed to copy email");
//     }
//   };

//   // Format date
//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formattedDate = date.toLocaleDateString('en-GB', {
//       day: '2-digit',
//       month: '2-digit',
//       year: 'numeric'
//     });
//     const formattedTime = date.toLocaleTimeString('en-US', {
//       hour: '2-digit',
//       minute: '2-digit',
//       hour12: false
//     });
//     return { date: formattedDate, time: formattedTime };
//   };

//   // Handle page change
//   const handlePageChange = (newPage) => {
//     if (newPage >= 1 && newPage <= pagination.totalPages && !loading) {
//       fetchClientData(newPage);
//     }
//   };

//   // Generate pagination range
//   const getPaginationRange = () => {
//     const delta = 2;
//     const range = [];
//     const rangeWithDots = [];

//     for (
//       let i = Math.max(2, pagination.currentPage - delta);
//       i <= Math.min(pagination.totalPages - 1, pagination.currentPage + delta);
//       i++
//     ) {
//       range.push(i);
//     }

//     if (pagination.currentPage - delta > 2) {
//       rangeWithDots.push(1, '...');
//     } else {
//       rangeWithDots.push(1);
//     }

//     rangeWithDots.push(...range);

//     if (pagination.currentPage + delta < pagination.totalPages - 1) {
//       rangeWithDots.push('...', pagination.totalPages);
//     } else {
//       if (pagination.totalPages > 1) {
//         rangeWithDots.push(pagination.totalPages);
//       }
//     }

//     return rangeWithDots;
//   };

//   // Fetch initial data
//   useEffect(() => {
//     fetchClientData();
//   }, []);

//   return (
//     <>
//       {loading  && <Loader />}
//       <section className="datatable clientsdatable">
//         <div className="table-responsive">

//           <table className="table">
//             <thead>
//               <tr>
//                 <th>
//                   <div className="parent-tag">
//                     Date Added
//                   </div>
//                 </th>
//                 <th>Email</th>
//                 <th>Name</th>
//                 <th>Project Assigned</th>
//                 <th className="text-center">CSV Permission</th>
//                 <th className="text-center">Activate/Deactivate</th>
//               </tr>
//             </thead>
//             <tbody>
//               {clientData.length === 0 && !loading ? (
//                 <tr>
//                   <td colSpan="6" className="text-center py-4">
//                     No clients found
//                   </td>
//                 </tr>
//               ) : (
//                 clientData.map((client) => {
//                   const { date, time } = formatDate(client.createdAt);
//                   return (
//                     <tr key={client._id}>
//                       <td>
//                         <div className="twice-text">
//                           <h6>{date}</h6>
//                           <p>{time}</p>
//                         </div>
//                       </td>
//                       <td>
//                         <p className="codepara">
//                           {client.email}
//                           <svg
//                             xmlns="http://www.w3.org/2000/svg"
//                             width="15"
//                             height="16"
//                             viewBox="0 0 15 16"
//                             fill="none"
//                             style={{ cursor: "pointer", marginLeft: "8px" }}
//                             onClick={() => copyToClipboard(client.email)}
//                           >
//                             <path
//                               d="M10.0139 8.49985L10.0402 10.9497C10.0622 12.9913 9.25441 13.8167 7.21286 13.8386L4.763 13.865C2.72145 13.887 1.89604 13.0792 1.87405 11.0376L1.84767 8.58778C1.82569 6.54623 2.63351 5.72082 4.67506 5.69884L7.12492 5.67246C9.16647 5.65047 9.99188 6.4583 10.0139 8.49985Z"
//                               stroke="#77868B"
//                               strokeWidth="1.2"
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                             />
//                             <path
//                               d="M13.476 4.96235L13.5024 7.41221C13.5244 9.45376 12.7166 10.2792 10.675 10.3012L10.0334 10.3081L10.0139 8.49984C9.99193 6.45829 9.16652 5.65046 7.12497 5.67245L5.31674 5.69192L5.30983 5.05029C5.28785 3.00874 6.09567 2.18333 8.13722 2.16135L10.5871 2.13496C12.6286 2.11298 13.454 2.92081 13.476 4.96235Z"
//                               stroke="#77868B"
//                               strokeWidth="1.2"
//                               strokeLinecap="round"
//                               strokeLinejoin="round"
//                             />
//                           </svg>
//                         </p>
//                       </td>
//                       <td>{client.name || 'N/A'}</td>
//                       <td>
//                         <div className="maintoken">
//                           <div className="tokenimg">
//                             <img
//                               src={`/assets/${client.privateSale.toLowerCase()}.svg`}
//                               alt="project logo"
//                               className="innerimg"
//                               onError={(e) => {
//                                 e.target.src = "/assets/default.svg"; // fallback image
//                               }}
//                             />
//                           </div>
//                           <p className="tokenpara">{client.privateSale}</p>
//                         </div>
//                       </td>
//                       <td>
//                         <div className="parent-toggle-switch-custom tabletoggle">
//                           <div className="custom-toggle">
//                             <input
//                               type="checkbox"
//                               className="checkbox"
//                               id={`csv-toggle-${client._id}`}
//                               checked={client.isCsvAllowed}
//                               onChange={() => toggleCsvPermission(client._id, client.isCsvAllowed)}
//                               disabled={loadingToggles[`csv_${client._id}`]}
//                             />
//                             <label className="switch" htmlFor={`csv-toggle-${client._id}`}>
//                               <span className="slider"></span>
//                             </label>
//                           </div>
//                           {/* {loadingToggles[`csv_${client._id}`] && (
//                             <div className="toggle-loading">...</div>
//                           )} */}
//                         </div>
//                       </td>
//                       <td>
//                         <div className="parent-toggle-switch-custom tabletoggle">
//                           <div className="custom-toggle">
//                             <input
//                               type="checkbox"
//                               className="checkbox"
//                               id={`status-toggle-${client._id}`}
//                               checked={client.isEnabled}
//                               onChange={() => toggleClientStatus(client._id, client.isEnabled)}
//                               disabled={loadingToggles[`status_${client._id}`]}
//                             />
//                             <label className="switch" htmlFor={`status-toggle-${client._id}`}>
//                               <span className="slider"></span>
//                             </label>
//                           </div>
//                           {/* {loadingToggles[`status_${client._id}`] && (
//                             <div className="toggle-loading">...</div>
//                           )} */}
//                         </div>
//                       </td>
//                     </tr>
//                   );
//                 })
//               )}
//             </tbody>
//           </table>
//         </div>

//         {clientData.length > 0 && (
//           <div className="footer-content">
//             <div className="left-f">
//               <h6>
//                 SHOWING {((pagination.currentPage - 1) * pagination.limit) + 1}-
//                 {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)} OF {pagination.totalCount}
//               </h6>
//             </div>
//             <div className="right-f">
//               <nav aria-label="Page navigation">
//                 <ul className="pagination">
//                   <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
//                     <button
//                       className="page-link"
//                       onClick={() => handlePageChange(pagination.currentPage - 1)}
//                       disabled={pagination.currentPage === 1 || loading}
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         width="17"
//                         height="17"
//                         viewBox="0 0 17 17"
//                         fill="none"
//                       >
//                         <path
//                           d="M7.88904 2.43555L5.38763 2.43555L1.99459 8.49682L4.496 8.49682L7.88904 2.43555Z"
//                           fill="#869397"
//                         />
//                         <path
//                           d="M7.88904 14.5703L5.38763 14.5703L1.99459 8.50904L3.57108 7.46117L4.496 8.50904L7.88904 14.5703Z"
//                           fill="#869397"
//                         />
//                         <path
//                           d="M8.01655 7.35742H14.9616L13.6869 9.64313H6.74182L8.01655 7.35742Z"
//                           fill="#869397"
//                         />
//                       </svg>
//                     </button>
//                   </li>

//                   {getPaginationRange().map((page, index) => (
//                     <li
//                       key={index}
//                       className={`page-item ${page === pagination.currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
//                     >
//                       {page === '...' ? (
//                         <span className="page-link">...</span>
//                       ) : (
//                         <button
//                           className="page-link"
//                           onClick={() => handlePageChange(page)}
//                           disabled={loading}
//                         >
//                           {page}
//                         </button>
//                       )}
//                     </li>
//                   ))}

//                   <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
//                     <button
//                       className="page-link"
//                       onClick={() => handlePageChange(pagination.currentPage + 1)}
//                       disabled={pagination.currentPage === pagination.totalPages || loading}
//                     >
//                       <svg
//                         xmlns="http://www.w3.org/2000/svg"
//                         width="17"
//                         height="17"
//                         viewBox="0 0 17 17"
//                         fill="none"
//                       >
//                         <path
//                           d="M9.11108 2.43555L11.6125 2.43555L15.0055 8.49682L12.5041 8.49682L9.11108 2.43555Z"
//                           fill="#329879"
//                         />
//                         <path
//                           d="M9.11108 14.5703L11.6125 14.5703L15.0055 8.50904L13.429 7.46117L12.5041 8.50904L9.11108 14.5703Z"
//                           fill="#329879"
//                         />
//                         <path
//                           d="M3.31318 7.35742H10.2582L8.98351 9.64313H2.03845L3.31318 7.35742Z"
//                           fill="#329879"
//                         />
//                       </svg>
//                     </button>
//                   </li>
//                 </ul>
//               </nav>
//             </div>
//           </div>
//         )}
//         <div className="mobileaccordions d-none">
//           <div className="inneracc">
//             <div className="accheader">
//               <div className="accmain">
//                 <p className="accpara">Date Added</p>
//                 <h6 className="acchead">03/09/2024 5:14</h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Email</p>
//                 <h6 className="acchead">
//                   johndoe@gmail.com
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     width="15"
//                     height="15"
//                     viewBox="0 0 15 15"
//                     fill="none"
//                   >
//                     <path
//                       d="M9.41377 7.57483L9.44015 10.0247C9.46213 12.0662 8.65431 12.8916 6.61276 12.9136L4.1629 12.94C2.12135 12.962 1.29594 12.1542 1.27396 10.1126L1.24757 7.66276C1.22559 5.62122 2.03342 4.7958 4.07496 4.77382L6.52482 4.74744C8.56637 4.72545 9.39178 5.53328 9.41377 7.57483Z"
//                       stroke="#77868B"
//                       stroke-width="1.2"
//                       stroke-linecap="round"
//                       stroke-linejoin="round"
//                     />
//                     <path
//                       d="M12.8759 4.03735L12.9023 6.48721C12.9243 8.52876 12.1165 9.35417 10.0749 9.37616L9.43329 9.38306L9.41382 7.57484C9.39183 5.53329 8.56642 4.72546 6.52487 4.74745L4.71664 4.76692L4.70973 4.12529C4.68775 2.08374 5.49557 1.25833 7.53712 1.23634L9.98698 1.20996C12.0285 1.18798 12.8539 1.9958 12.8759 4.03735Z"
//                       stroke="#77868B"
//                       stroke-width="1.2"
//                       stroke-linecap="round"
//                       stroke-linejoin="round"
//                     />
//                   </svg>
//                 </h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Name</p>
//                 <h6 className="acchead">John Doe</h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Project Assigned</p>
//                 <div className="mainproject">
//                   <div className="projectimg">
//                     <img src="/assets/dop.svg" alt="innerimg" className="innerimg" />
//                   </div>
//                   <p className="projectpara">DOP</p>
//                 </div>
//               </div>
//             </div>
//             <Accordion activeKey={openKey}>
//               <Accordion.Collapse eventKey="0">
//                 <div className="maincontent">
//                   <div className="innercontent">
//                     <p className="contentpara">CSV Permission</p>
//                     <div className="parent-toggle-switch-custom tabletoggle">
//                       <div className="custom-toggle">
//                         <input
//                           type="checkbox"
//                           className="checkbox"
//                           id="toggle1"
//                           checked={toggles.toggle1}
//                           onChange={() => handleToggle("toggle1")}
//                         />
//                         <label className="switch" htmlFor="toggle1">
//                           <span className="slider"></span>
//                         </label>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="innercontent">
//                     <p className="contentpara">Activate/Deactivate</p>
//                     <div className="parent-toggle-switch-custom tabletoggle">
//                       <div className="custom-toggle">
//                         <input
//                           type="checkbox"
//                           className="checkbox"
//                           id="toggle2"
//                           checked={toggles.toggle2}
//                           onChange={() => handleToggle("toggle2")}
//                         />
//                         <label className="switch" htmlFor="toggle2">
//                           <span className="slider"></span>
//                         </label>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </Accordion.Collapse>
//             </Accordion>
//             <button
//               onClick={() => toggle('0')}
//               variant="link"
//               className="accbutton"
//             >
//               {openKey === '0' ? "Less Info" : "More Info"}
//               {openKey === '0' ? (
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   width="10"
//                   height="6"
//                   viewBox="0 0 10 6"
//                   fill="none"
//                 >
//                   <path
//                     d="M8.62344 5.22141H4.80933H1.3748C0.787069 5.22141 0.493206 4.44081 0.909513 3.98322L4.08079 0.497446C4.58893 -0.0610847 5.41543 -0.0610847 5.92356 0.497446L7.12963 1.82312L9.09485 3.98322C9.50503 4.44081 9.21117 5.22141 8.62344 5.22141Z"
//                     fill="#02171D"
//                   />
//                 </svg>
//               ) : (
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   width="9"
//                   height="6"
//                   viewBox="0 0 9 6"
//                   fill="none"
//                 >
//                   <path
//                     d="M8.12344 0.228516H4.30933H0.874797C0.287069 0.228516 -0.00679424 1.00911 0.409513 1.4667L3.58079 4.95247C4.08893 5.51101 4.91543 5.51101 5.42356 4.95247L6.62963 3.62681L8.59485 1.4667C9.00503 1.00911 8.71117 0.228516 8.12344 0.228516Z"
//                     fill="#02171D"
//                   />
//                 </svg>
//               )}
//             </button>
//           </div>
//           <div className="inneracc">
//             <div className="accheader">
//               <div className="accmain">
//                 <p className="accpara">Date Added</p>
//                 <h6 className="acchead">03/09/2024 5:14</h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Email</p>
//                 <h6 className="acchead">
//                   johndoe@gmail.com
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     width="15"
//                     height="15"
//                     viewBox="0 0 15 15"
//                     fill="none"
//                   >
//                     <path
//                       d="M9.41377 7.57483L9.44015 10.0247C9.46213 12.0662 8.65431 12.8916 6.61276 12.9136L4.1629 12.94C2.12135 12.962 1.29594 12.1542 1.27396 10.1126L1.24757 7.66276C1.22559 5.62122 2.03342 4.7958 4.07496 4.77382L6.52482 4.74744C8.56637 4.72545 9.39178 5.53328 9.41377 7.57483Z"
//                       stroke="#77868B"
//                       stroke-width="1.2"
//                       stroke-linecap="round"
//                       stroke-linejoin="round"
//                     />
//                     <path
//                       d="M12.8759 4.03735L12.9023 6.48721C12.9243 8.52876 12.1165 9.35417 10.0749 9.37616L9.43329 9.38306L9.41382 7.57484C9.39183 5.53329 8.56642 4.72546 6.52487 4.74745L4.71664 4.76692L4.70973 4.12529C4.68775 2.08374 5.49557 1.25833 7.53712 1.23634L9.98698 1.20996C12.0285 1.18798 12.8539 1.9958 12.8759 4.03735Z"
//                       stroke="#77868B"
//                       stroke-width="1.2"
//                       stroke-linecap="round"
//                       stroke-linejoin="round"
//                     />
//                   </svg>
//                 </h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Name</p>
//                 <h6 className="acchead">John Doe</h6>
//               </div>
//               <div className="accmain">
//                 <p className="accpara">Project Assigned</p>
//                 <div className="mainproject">
//                   <div className="projectimg">
//                     <img src="/assets/dop.svg" alt="innerimg" className="innerimg" />
//                   </div>
//                   <p className="projectpara">DOP</p>
//                 </div>
//               </div>
//             </div>
//             <Accordion activeKey={openKey}>
//               <Accordion.Collapse eventKey="1">
//                 <div className="maincontent">
//                   <div className="innercontent">
//                     <p className="contentpara">CSV Permission</p>
//                     <div className="parent-toggle-switch-custom tabletoggle">
//                       <div className="custom-toggle">
//                         <input
//                           type="checkbox"
//                           className="checkbox"
//                           id="toggle1"
//                           checked={toggles.toggle1}
//                           onChange={() => handleToggle("toggle1")}
//                         />
//                         <label className="switch" htmlFor="toggle1">
//                           <span className="slider"></span>
//                         </label>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="innercontent">
//                     <p className="contentpara">Activate/Deactivate</p>
//                     <div className="parent-toggle-switch-custom tabletoggle">
//                       <div className="custom-toggle">
//                         <input
//                           type="checkbox"
//                           className="checkbox"
//                           id="toggle2"
//                           checked={toggles.toggle2}
//                           onChange={() => handleToggle("toggle2")}
//                         />
//                         <label className="switch" htmlFor="toggle2">
//                           <span className="slider"></span>
//                         </label>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </Accordion.Collapse>
//             </Accordion>
//             <button
//               onClick={() => toggle('1')}
//               variant="link"
//               className="accbutton"
//             >
//               {openKey === '1' ? "Less Info" : "More Info"}
//               {openKey === '1' ? (
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   width="10"
//                   height="6"
//                   viewBox="0 0 10 6"
//                   fill="none"
//                 >
//                   <path
//                     d="M8.62344 5.22141H4.80933H1.3748C0.787069 5.22141 0.493206 4.44081 0.909513 3.98322L4.08079 0.497446C4.58893 -0.0610847 5.41543 -0.0610847 5.92356 0.497446L7.12963 1.82312L9.09485 3.98322C9.50503 4.44081 9.21117 5.22141 8.62344 5.22141Z"
//                     fill="#02171D"
//                   />
//                 </svg>
//               ) : (
//                 <svg
//                   xmlns="http://www.w3.org/2000/svg"
//                   width="9"
//                   height="6"
//                   viewBox="0 0 9 6"
//                   fill="none"
//                 >
//                   <path
//                     d="M8.12344 0.228516H4.30933H0.874797C0.287069 0.228516 -0.00679424 1.00911 0.409513 1.4667L3.58079 4.95247C4.08893 5.51101 4.91543 5.51101 5.42356 4.95247L6.62963 3.62681L8.59485 1.4667C9.00503 1.00911 8.71117 0.228516 8.12344 0.228516Z"
//                     fill="#02171D"
//                   />
//                 </svg>
//               )}
//             </button>
//           </div>
//         </div>
//       </section>
//     </>
//   );
// };

// export default DataTable;

// import React, { useState } from "react";
// import "./datatable.scss";
// import { Accordion, Card, ProgressBar } from "react-bootstrap";

// const DataTable = () => {
//   const [toggles, setToggles] = useState({
//     toggle1: false,
//     toggle2: false,
//     toggle3: false,
//   });

//   const handleToggle = (key) => {
//     setToggles((prev) => ({
//       ...prev,
//       [key]: !prev[key],
//     }));
//   };

//   return (
//     <>
//       <section className="datatable clientsdatable">
//         <div className="table-responsive">
//           <table class="table">
//             <thead>
//               <tr>
//                 <th>
//                   <div className="parent-tag">
//                     Date Added

//                   </div>
//                 </th>
//                 <th>Email</th>
//                 <th>Name</th>
//                 <th>Project Assigned</th>
//                 <th className="text-center">CSV Permission</th>
//                 <th className="text-center">Activate/Deactivate</th>
//               </tr>
//             </thead>
//             <tbody>
//               <tr>
//                 <td>
//                   <div className="twice-text">
//                     <h6>03/09/2024</h6>
//                     <p>5:14</p>
//                   </div>
//                 </td>
//                 <td>
//                   <p className="codepara">
//                     johndoe@gmail.com
//                     <svg
//                     //copy to clipboard
//                       xmlns="http://www.w3.org/2000/svg"
//                       width="15"
//                       height="16"
//                       viewBox="0 0 15 16"
//                       fill="none"
//                       cursor="pointer"
//                     >
//                       <path
//                         d="M10.0139 8.49985L10.0402 10.9497C10.0622 12.9913 9.25441 13.8167 7.21286 13.8386L4.763 13.865C2.72145 13.887 1.89604 13.0792 1.87405 11.0376L1.84767 8.58778C1.82569 6.54623 2.63351 5.72082 4.67506 5.69884L7.12492 5.67246C9.16647 5.65047 9.99188 6.4583 10.0139 8.49985Z"
//                         stroke="#77868B"
//                         stroke-width="1.2"
//                         stroke-linecap="round"
//                         stroke-linejoin="round"
//                       />
//                       <path
//                         d="M13.476 4.96235L13.5024 7.41221C13.5244 9.45376 12.7166 10.2792 10.675 10.3012L10.0334 10.3081L10.0139 8.49984C9.99193 6.45829 9.16652 5.65046 7.12497 5.67245L5.31674 5.69192L5.30983 5.05029C5.28785 3.00874 6.09567 2.18333 8.13722 2.16135L10.5871 2.13496C12.6286 2.11298 13.454 2.92081 13.476 4.96235Z"
//                         stroke="#77868B"
//                         stroke-width="1.2"
//                         stroke-linecap="round"
//                         stroke-linejoin="round"
//                       />
//                     </svg>
//                   </p>
//                 </td>
//                 <td>John Doe</td>
//                 <td>
//                   <div className="maintoken">
//                     <div className="tokenimg">
//                       <img src="/assets/dop.svg" alt="innerimg" className="innerimg" />
//                     </div>
//                     <p className="tokenpara">DOP</p>
//                   </div>
//                 </td>
//                 <td>
//                   <div className="parent-toggle-switch-custom tabletoggle">
//                     <div className="custom-toggle">
//                       <input
//                         type="checkbox"
//                         className="checkbox"
//                         id="toggle1"
//                         checked={toggles.toggle1}
//                         onChange={() => handleToggle("toggle1")}
//                       />
//                       <label className="switch" htmlFor="toggle1">
//                         <span className="slider"></span>
//                       </label>
//                     </div>
//                   </div>
//                 </td>
//                 <td>
//                   <div className="parent-toggle-switch-custom tabletoggle">
//                     <div className="custom-toggle">
//                       <input
//                         type="checkbox"
//                         className="checkbox"
//                         id="toggle2"
//                         checked={toggles.toggle2}
//                         onChange={() => handleToggle("toggle2")}
//                       />
//                       <label className="switch" htmlFor="toggle2">
//                         <span className="slider"></span>
//                       </label>
//                     </div>
//                   </div>
//                 </td>
//               </tr>
//             </tbody>
//           </table>
//         </div>
//         <div className="footer-content">
//           <div className="left-f">
//             <h6>SHOWING 1-10 OF 145</h6>
//           </div>
//           <div className="right-f">
//             <nav aria-label="Page navigation example">
//               <ul class="pagination">
//                 <li class="page-item">
//                   <a class="page-link" href="#">
//                    previous
//                   </a>
//                 </li>
//                 <li class="page-item">
//                   <a class="page-link active" href="#">
//                     1
//                   </a>
//                 </li>
//                 <li class="page-item">
//                   <a class="page-link" href="#">
//                     2
//                   </a>
//                 </li>
//                 <li class="page-item">
//                   <a class="page-link" href="#">
//                    next
//                   </a>
//                 </li>
//               </ul>
//             </nav>
//           </div>
//         </div>

//       </section>
//     </>
//   );
// };

// export default DataTable;
